import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiArrowRight, FiMinus, FiPlus } from 'react-icons/fi';

function NumberQuestion({ question, value, onChange }) {
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const delayRef = useRef(null);
  const intervalRef = useRef(null);

  valueRef.current = value;
  onChangeRef.current = onChange;

  const stopRepeating = () => {
    window.clearTimeout(delayRef.current);
    window.clearInterval(intervalRef.current);
    delayRef.current = null;
    intervalRef.current = null;
  };

  useEffect(() => () => {
    window.clearTimeout(delayRef.current);
    window.clearInterval(intervalRef.current);
  }, []);

  const changeBy = (direction) => {
    const next = Math.min(question.max, Math.max(question.min, valueRef.current + direction * question.step));
    if (next !== valueRef.current) {
      valueRef.current = next;
      onChangeRef.current(next);
    }
  };

  const startRepeating = (event, direction) => {
    if (event.button !== 0 || event.currentTarget.disabled) return;
    stopRepeating();
    changeBy(direction);
    delayRef.current = window.setTimeout(() => {
      intervalRef.current = window.setInterval(() => changeBy(direction), 85);
    }, 380);
  };

  const stepButton = (direction, label, Icon) => (
    <button
      type="button"
      aria-label={label}
      disabled={direction < 0 ? value <= question.min : value >= question.max}
      onPointerDown={(event) => startRepeating(event, direction)}
      onPointerUp={stopRepeating}
      onPointerCancel={stopRepeating}
      onPointerLeave={stopRepeating}
      onBlur={stopRepeating}
      onClick={(event) => {
        // Pointer clicks are handled on pointerdown. Native keyboard clicks have detail 0.
        if (event.detail === 0) changeBy(direction);
      }}
    >
      <SafeIcon icon={Icon} />
    </button>
  );

  return (
    <div className="number-panel">
      <div className="number-stepper">
        {stepButton(-1, 'Decrease value', FiMinus)}
        <div className="number-display" aria-live="off">
          <span>{question.prefix || ''}{value.toLocaleString('en-AU')}</span>
          {question.suffix && <small>{question.suffix}</small>}
        </div>
        {stepButton(1, 'Increase value', FiPlus)}
      </div>
      {question.helper && <p className="helper-copy">{question.helper}</p>}
      <div className="quick-picks">
        <span>Quick select</span>
        <div>
          {(question.quickPicks||[]).filter(pick=>pick>=question.min&&pick<=question.max).map((pick) => (
            <button
              className={value === pick ? 'quick-pick selected' : 'quick-pick'}
              key={pick}
              onClick={() => { stopRepeating(); valueRef.current = pick; onChange(pick); }}
              type="button"
            >
              {question.prefix || ''}{pick}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuestionScreen({ question, index, answer, onAnswer, onNext }) {
  const [selected, setSelected] = useState(answer || '');
  const [numberValue, setNumberValue] = useState(Number(answer) || 0);

  useEffect(() => {
    setSelected(answer || '');
    setNumberValue(Number(answer) || 0);
  }, [answer, question.id]);

  const choose = (option) => {
    setSelected(option);
    onAnswer(question.id, option);
    window.setTimeout(onNext, 300);
  };

  return (
    <motion.section
      className="question-screen"
      key={question.id}
      initial={{ opacity: 0, x: 12 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="question-kicker">QUESTION {String(index + 1).padStart(2, '0')}</div>
      <h1>{question.prompt}</h1>
      {question.type === 'choice' ? (
        <div className="answer-list">
          {question.options.map((option, optionIndex) => (
            <button
              className={`answer-card ${selected === option ? 'is-selected' : ''}`}
              key={option}
              onClick={() => choose(option)}
              type="button"
            >
              <span className="answer-index">{String.fromCharCode(65 + optionIndex)}</span>
              <span>{option}</span>
              <span className="answer-radio" />
            </button>
          ))}
        </div>
      ) : (
        <>
          <NumberQuestion
            question={question}
            value={numberValue}
            onChange={(next) => {
              setNumberValue(next);
              onAnswer(question.id, next);
            }}
          />
          <button className="primary-button question-next" onClick={onNext} type="button">
            Continue
            <SafeIcon icon={FiArrowRight} aria-hidden="true" />
          </button>
        </>
      )}
    </motion.section>
  );
}

export default QuestionScreen;